import { useState, useEffect } from 'react';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import {
  Container,
  Tabs,
  Card,
  Stack,
  Text,
  Title,
  Grid,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Button,
  Group,
  Divider,
  Alert,
  MultiSelect,
  Chip,
  Badge
} from '@mantine/core';
import {
  IconUser,
  IconPhoto,
  IconInfoCircle,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconCrown,
  IconRosetteDiscountCheck
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import ImageUploader from '../components/profile/ImageUploader';
import { useApi, useApiClient, useSession, mediaUrl } from '../api';
import { uploadProfileImage } from '../api/uploads';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUsd = (cents) => (cents == null ? '' : usd.format(cents / 100));

const footAttributes = [
  { value: 'small-feet', label: 'Small Feet' },
  { value: 'large-feet', label: 'Large Feet' },
  { value: 'medium-feet', label: 'Medium Feet' },
  { value: 'petite-feet', label: 'Petite Feet' },
  { value: 'big-feet', label: 'Big Feet' },
  { value: 'fat-feet', label: 'Fat Feet' },
  { value: 'thin-feet', label: 'Thin Feet' },
  { value: 'wide-feet', label: 'Wide Feet' },
  { value: 'narrow-feet', label: 'Narrow Feet' },
  { value: 'curvy-feet', label: 'Curvy Feet' },
  { value: 'tan-feet', label: 'Tan Feet' },
  { value: 'fair-feet', label: 'Fair Feet' },
  { value: 'dark-feet', label: 'Dark Feet' },
  { value: 'olive-feet', label: 'Olive Feet' },
  { value: 'brown-feet', label: 'Brown Feet' },
  { value: 'pale-feet', label: 'Pale Feet' },
  { value: 'long-toes', label: 'Long Toes' },
  { value: 'short-toes', label: 'Short Toes' },
  { value: 'round-toes', label: 'Round Toes' },
  { value: 'square-toes', label: 'Square Toes' },
  { value: 'pointed-toes', label: 'Pointed Toes' },
  { value: 'oval-toes', label: 'Oval Toes' },
  { value: 'missing-toe', label: 'Missing Toe' },
  { value: 'extra-toe', label: 'Extra Toe' },
  { value: 'webbed-toes', label: 'Webbed Toes' },
  { value: 'curled-toes', label: 'Curled Toes' },
  { value: 'bunions', label: 'Bunions' },
  { value: 'red-nail-polish', label: 'Red Nail Polish' },
  { value: 'pink-nail-polish', label: 'Pink Nail Polish' },
  { value: 'purple-nail-polish', label: 'Purple Nail Polish' },
  { value: 'blue-nail-polish', label: 'Blue Nail Polish' },
  { value: 'black-nail-polish', label: 'Black Nail Polish' },
  { value: 'white-nail-polish', label: 'White Nail Polish' },
  { value: 'gold-nail-polish', label: 'Gold Nail Polish' },
  { value: 'silver-nail-polish', label: 'Silver Nail Polish' },
  { value: 'nude-nail-polish', label: 'Nude Nail Polish' },
  { value: 'neon-nail-polish', label: 'Neon Nail Polish' },
  { value: 'glitter-nail-polish', label: 'Glitter Nail Polish' },
  { value: 'matte-nail-polish', label: 'Matte Nail Polish' },
  { value: 'french-manicure', label: 'French Manicure' },
  { value: 'gel-nails', label: 'Gel Nails' },
  { value: 'acrylic-nails', label: 'Acrylic Nails' },
  { value: 'nail-art', label: 'Nail Art' },
  { value: 'rhinestones', label: 'Rhinestones' },
  { value: 'charms', label: 'Charms' },
  { value: 'studs', label: 'Studs' },
  { value: 'gradient-ombres', label: 'Gradient/Ombré' },
  { value: 'marble-nails', label: 'Marble Nails' },
  { value: 'chrome-nails', label: 'Chrome Nails' },
  { value: 'floral-nails', label: 'Floral Nails' },
  { value: 'animal-print', label: 'Animal Print' },
  { value: 'spa-pedicure', label: 'Spa Pedicure' },
  { value: 'classic-pedicure', label: 'Classic Pedicure' },
  { value: 'gel-pedicure', label: 'Gel Pedicure' },
  { value: 'acupuncture-pedicure', label: 'Acupuncture Pedicure' },
  { value: 'hot-stone-pedicure', label: 'Hot Stone Pedicure' },
  { value: 'fish-pedicure', label: 'Fish Pedicure' },
  { value: 'paraffin-pedicure', label: 'Paraffin Pedicure' },
  { value: 'tattoos', label: 'Tattoos' },
  { value: 'piercings', label: 'Piercings' },
  { value: 'foot-jewelry', label: 'Foot Jewelry' },
  { value: 'ankle-bracelets', label: 'Ankle Bracelets' },
  { value: 'toe-rings', label: 'Toe Rings' },
  { value: 'pedicure-extras', label: 'Pedicure Extras' },
  { value: 'soft-soles', label: 'Soft Soles' },
  { value: 'rough-soles', label: 'Rough Soles' },
  { value: 'calloused-soles', label: 'Calloused Soles' },
  { value: 'smooth-soles', label: 'Smooth Soles' },
  { value: 'ticklish-feet', label: 'Ticklish Feet' },
  { value: 'arched-feet', label: 'Arched Feet' },
  { value: 'flat-feet', label: 'Flat Feet' },
  { value: 'high-arches', label: 'High Arches' },
  { value: 'moisturized-feet', label: 'Moisturized Feet' },
  { value: 'dry-feet', label: 'Dry Feet' },
  { value: 'perfumed-feet', label: 'Perfumed Feet' },
  { value: 'fresh-feet', label: 'Fresh Feet' },
  { value: 'dirty-feet', label: 'Dirty Feet' },
  { value: 'sweaty-feet', label: 'Sweaty Feet' },
  { value: 'warm-feet', label: 'Warm Feet' },
  { value: 'cool-feet', label: 'Cool Feet' },
  { value: 'barefoot', label: 'Barefoot' },
  { value: 'socks', label: 'Socks' },
  { value: 'stockings', label: 'Stockings' },
  { value: 'flip-flops', label: 'Flip Flops' },
  { value: 'heels', label: 'Heels' },
  { value: 'high-heels', label: 'High Heels' },
  { value: 'stilettos', label: 'Stilettos' },
  { value: 'flats', label: 'Flats' },
  { value: 'sandals', label: 'Sandals' },
  { value: 'boots', label: 'Boots' },
  { value: 'pregnant-feet', label: 'Pregnant Feet' },
  { value: 'cosplay-feet', label: 'Cosplay Feet' },
  { value: 'roleplay', label: 'Roleplay' },
  { value: 'foot-worship', label: 'Foot Worship' },
  { value: 'custom-requests', label: 'Custom Requests' },
];

// Empty strings become null at the API boundary (contract §1.1 — nullable
// fields are null, never coerced "")
const orNull = (value) => (value === '' || value === undefined ? null : value);

// Map a ProfileImage (contract §3.3) into the ImageUploader item shape
const toUploaderImage = (img) => ({
  id: img.id,
  preview: mediaUrl(img.image_path),
  uploaded: true,
  fromServer: true,
  is_primary: img.is_primary || false
});

const ProfilePage = () => {
  const api = useApiClient();
  const { me } = useSession();

  const {
    data: profileData,
    error: profileError,
    refetch: refetchProfile
  } = useApi('/api/v1/me/profile');

  const { data: subscriptionData } = useApi('/api/v1/me/subscription');
  const { data: plansData } = useApi('/api/v1/subscription/plans');
  const { data: userData } = useApi('/api/v1/me/user');

  // Profile state
  const [profileImages, setProfileImages] = useState([]);
  const [leftFootImages, setLeftFootImages] = useState([]);
  const [rightFootImages, setRightFootImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Display name (separate from profile, stored on User model)
  const [displayName, setDisplayName] = useState('');
  const [displayNameSaving, setDisplayNameSaving] = useState(false);

  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    age: '',
    weight: '',
    height: '',
    race: '',
    numberOfToes: 10,
    bio: ''
  });

  // Location & Demographics
  const [demographics, setDemographics] = useState({
    country: '',
    state: '',
    city: '',
    zipCode: '',
    occupation: '',
    workoutFrequency: '',
    hobbies: [],
    interests: [],
    education: '',
    relationshipStatus: '',
    categories: []
  });

  // Populate display name from the user record
  useEffect(() => {
    if (userData?.display_name) {
      setDisplayName(userData.display_name);
    }
  }, [userData]);

  // Populate form state when profile data arrives
  useEffect(() => {
    if (!profileData) return;
    const profile = profileData;

    setPersonalInfo({
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      age: profile.age ?? '',
      weight: profile.weight ?? '',
      height: profile.height || '',
      race: profile.race || '',
      numberOfToes: profile.number_of_toes ?? 10,
      bio: profile.bio || ''
    });

    setDemographics({
      country: profile.country || '',
      state: profile.state || '',
      city: profile.city || '',
      zipCode: profile.zip_code || '',
      occupation: profile.occupation || '',
      workoutFrequency: profile.workout_frequency || '',
      hobbies: profile.hobbies || [],
      interests: profile.interests || [],
      education: profile.education || '',
      relationshipStatus: profile.relationship_status || '',
      categories: profile.categories || []
    });

    // Single images array grouped by image_type (contract §3.3)
    const images = profile.images || [];
    setProfileImages(images.filter(img => img.image_type === 'profile').map(toUploaderImage));
    setLeftFootImages(images.filter(img => img.image_type === 'left_foot').map(toUploaderImage));
    setRightFootImages(images.filter(img => img.image_type === 'right_foot').map(toUploaderImage));
  }, [profileData]);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDemographicsChange = (field, value) => {
    setDemographics(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ImageUploader removal only updates its list — diff against the previous
  // list and delete removed server-side images (DELETE -> 204).
  const makeImagesChangeHandler = (currentImages, setImages) => (nextImages) => {
    const nextIds = new Set(nextImages.map(img => img.id));
    const removedServerImages = currentImages.filter(
      img => img.fromServer && !nextIds.has(img.id)
    );
    setImages(nextImages);
    for (const img of removedServerImages) {
      api.del(`/api/v1/me/profile/images/${img.id}`).catch(err => {
        console.error('Failed to delete image:', err);
        notifications.show({
          title: 'Error',
          message: err?.message || 'Failed to delete image',
          color: 'red'
        });
        refetchProfile();
      });
    }
  };

  const handleSaveDisplayName = async () => {
    if (!displayName.trim()) return;
    setDisplayNameSaving(true);
    try {
      await api.post('/api/v1/me/display-name', { display_name: displayName.trim() });
      notifications.show({
        title: 'Display name updated',
        message: `Your display name is now "${displayName.trim()}"`,
        color: 'green'
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to update display name',
        color: 'red'
      });
    } finally {
      setDisplayNameSaving(false);
    }
  };

  const handleSetPrimaryImage = async (imageId, imageType) => {
    try {
      await api.put(`/api/v1/me/profile/images/${imageId}/primary`);
      // Update the local state to reflect the change
      if (imageType === 'profile') {
        setProfileImages(prev => prev.map(img => ({
          ...img,
          is_primary: img.id === imageId
        })));
      } else if (imageType === 'left_foot') {
        setLeftFootImages(prev => prev.map(img => ({
          ...img,
          is_primary: img.id === imageId
        })));
      } else if (imageType === 'right_foot') {
        setRightFootImages(prev => prev.map(img => ({
          ...img,
          is_primary: img.id === imageId
        })));
      }
      notifications.show({
        title: 'Success',
        message: 'Primary image updated successfully',
        color: 'green'
      });
    } catch (err) {
      console.error('Error setting primary image:', err);
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to update primary image',
        color: 'red'
      });
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);

    try {
      // Prepare profile data (contract §4.2 PATCH /me/profile)
      const data = {
        first_name: orNull(personalInfo.firstName),
        last_name: orNull(personalInfo.lastName),
        age: orNull(personalInfo.age),
        weight: orNull(personalInfo.weight),
        height: orNull(personalInfo.height),
        race: orNull(personalInfo.race),
        number_of_toes: personalInfo.numberOfToes ?? 10,
        bio: orNull(personalInfo.bio),
        country: orNull(demographics.country),
        state: orNull(demographics.state),
        city: orNull(demographics.city),
        zip_code: orNull(demographics.zipCode),
        occupation: orNull(demographics.occupation),
        workout_frequency: orNull(demographics.workoutFrequency),
        hobbies: demographics.hobbies,
        interests: demographics.interests,
        education: orNull(demographics.education),
        relationship_status: orNull(demographics.relationshipStatus),
        categories: demographics.categories
      };

      await api.patch('/api/v1/me/profile', data);

      // Upload any new images (multipart — contract §5)
      const pendingUploads = [
        ...profileImages.filter(img => img.file && !img.uploaded).map(img => ({ img, type: 'profile' })),
        ...leftFootImages.filter(img => img.file && !img.uploaded).map(img => ({ img, type: 'left_foot' })),
        ...rightFootImages.filter(img => img.file && !img.uploaded).map(img => ({ img, type: 'right_foot' })),
      ];

      if (pendingUploads.length > 0) {
        const results = await Promise.allSettled(
          pendingUploads.map(({ img, type }) => uploadProfileImage(api, img.file, type))
        );
        const failures = results.filter(r => r.status === 'rejected');
        if (failures.length > 0) {
          console.error('Some images failed to upload:', failures);
          notifications.show({
            title: 'Upload issue',
            message: `${failures.length} image(s) failed to upload`,
            color: 'red'
          });
        }
        // Re-sync so uploaded images get their server ids and paths
        refetchProfile();
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err?.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Subscription (contract §4.4: {subscription: Subscription | null})
  const subscription = subscriptionData?.subscription || null;
  const subscriptionActive = subscription?.status === 'active';
  const subscriptionPastDue = subscription?.status === 'past_due';
  const subscriptionPlanName =
    plansData?.items?.find(p => p.id === subscription?.plan)?.name || subscription?.plan;

  // Options for select fields
  const raceOptions = [
    { value: 'asian', label: 'Asian' },
    { value: 'black', label: 'Black or African American' },
    { value: 'hispanic', label: 'Hispanic or Latino' },
    { value: 'native', label: 'Native American' },
    { value: 'pacific', label: 'Pacific Islander' },
    { value: 'white', label: 'White' },
    { value: 'mixed', label: 'Mixed Race' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const workoutFrequencyOptions = [
    { value: 'never', label: 'Never' },
    { value: 'rarely', label: 'Rarely (less than once a month)' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: '2-3_times_week', label: '2-3 times per week' },
    { value: '4-5_times_week', label: '4-5 times per week' },
    { value: 'daily', label: 'Daily' },
    { value: 'multiple_daily', label: 'Multiple times daily' }
  ];

  const hobbyOptions = [
    { value: 'reading', label: 'Reading' },
    { value: 'cooking', label: 'Cooking' },
    { value: 'gardening', label: 'Gardening' },
    { value: 'photography', label: 'Photography' },
    { value: 'traveling', label: 'Traveling' },
    { value: 'music', label: 'Music' },
    { value: 'sports', label: 'Sports' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'art', label: 'Art & Crafts' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'dancing', label: 'Dancing' },
    { value: 'writing', label: 'Writing' },
    { value: 'hiking', label: 'Hiking' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'meditation', label: 'Meditation' },
    { value: 'movies', label: 'Movies & TV' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'technology', label: 'Technology' }
  ];

  const relationshipOptions = [
    { value: 'single', label: 'Single' },
    { value: 'dating', label: 'Dating' },
    { value: 'relationship', label: 'In a relationship' },
    { value: 'engaged', label: 'Engaged' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
    { value: 'complicated', label: 'It\'s complicated' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const educationOptions = [
    { value: 'high_school', label: 'High School' },
    { value: 'some_college', label: 'Some College' },
    { value: 'associates', label: 'Associate\'s Degree' },
    { value: 'bachelors', label: 'Bachelor\'s Degree' },
    { value: 'masters', label: 'Master\'s Degree' },
    { value: 'doctorate', label: 'Doctorate' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/">
      <Container size="lg" py="xl">
        <Stack gap="xl">
        <div>
          <Group gap="sm" align="center">
            <Title order={1}>My Profile</Title>
            {/* Buyer ID verification (contract §4.23) — badge-only */}
            {me?.identity_verified && (
              <Badge
                color="blue"
                variant="light"
                leftSection={<IconRosetteDiscountCheck size="0.8rem" />}
              >
                ID verified
              </Badge>
            )}
          </Group>
          <Text c="dimmed" mt="xs">
            Manage your personal information and photos
          </Text>
        </div>

        {saveSuccess && (
          <Alert color="green" title="Profile Saved!">
            Your profile has been updated successfully.
          </Alert>
        )}

        {(error || profileError) && (
          <Alert
            icon={<IconExclamationCircle size="1rem" />}
            color="red"
            title="Error"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error || profileError?.message}
          </Alert>
        )}

        <Tabs defaultValue="photos" variant="outline">
          <Tabs.List>
            <Tabs.Tab value="photos" leftSection={<IconPhoto size="0.8rem" />}>
              Photos
            </Tabs.Tab>
            <Tabs.Tab value="personal" leftSection={<IconUser size="0.8rem" />}>
              Personal Info
            </Tabs.Tab>
            <Tabs.Tab value="demographics" leftSection={<IconInfoCircle size="0.8rem" />}>
              Demographics
            </Tabs.Tab>
            <Tabs.Tab value="subscription" leftSection={<IconCrown size="0.8rem" />}>
              Subscription
            </Tabs.Tab>
          </Tabs.List>

          {/* Photos Tab */}
          <Tabs.Panel value="photos" pt="lg">
            <Stack gap="xl">
              <ImageUploader
                title="Profile Pictures"
                description="Upload up to 10 profile pictures that represent you"
                images={profileImages}
                onImagesChange={makeImagesChangeHandler(profileImages, setProfileImages)}
                maxImages={10}
                allowPrimarySelection={true}
                onSetPrimary={(imageId) => handleSetPrimaryImage(imageId, 'profile')}
              />

              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <ImageUploader
                    title="Left Foot Photos"
                    description="Upload up to 10 photos of your left foot"
                    images={leftFootImages}
                    onImagesChange={makeImagesChangeHandler(leftFootImages, setLeftFootImages)}
                    maxImages={10}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <ImageUploader
                    title="Right Foot Photos"
                    description="Upload up to 10 photos of your right foot"
                    images={rightFootImages}
                    onImagesChange={makeImagesChangeHandler(rightFootImages, setRightFootImages)}
                    maxImages={10}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Tabs.Panel>

          {/* Personal Information Tab */}
          <Tabs.Panel value="personal" pt="lg">
            <Stack gap="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Display Name</Title>
                <Text size="sm" c="dimmed">
                  This is the name other users see on your profile, messages, and requests.
                </Text>
                <Group align="flex-end">
                  <TextInput
                    label="Display Name"
                    placeholder="Choose a display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.currentTarget.value)}
                    maxLength={100}
                    style={{ flex: 1 }}
                  />
                  <Button
                    onClick={handleSaveDisplayName}
                    loading={displayNameSaving}
                    disabled={!displayName.trim() || displayName === userData?.display_name}
                  >
                    Save
                  </Button>
                </Group>
              </Stack>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Personal Information</Title>
                <Text size="sm" c="dimmed">
                  All fields are optional. Share as much or as little as you're comfortable with.
                </Text>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="First Name"
                      placeholder="Enter your first name"
                      value={personalInfo.firstName}
                      onChange={(e) => handlePersonalInfoChange('firstName', e.currentTarget.value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Last Name"
                      placeholder="Enter your last name"
                      value={personalInfo.lastName}
                      onChange={(e) => handlePersonalInfoChange('lastName', e.currentTarget.value)}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                      label="Age"
                      placeholder="Your age"
                      min={13}
                      max={120}
                      value={personalInfo.age}
                      onChange={(value) => handlePersonalInfoChange('age', value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                      label="Weight (lbs)"
                      placeholder="Your weight"
                      min={50}
                      max={1000}
                      value={personalInfo.weight}
                      onChange={(value) => handlePersonalInfoChange('weight', value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="Height"
                      placeholder="e.g., 5'6'', 170cm"
                      value={personalInfo.height}
                      onChange={(e) => handlePersonalInfoChange('height', e.currentTarget.value)}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Select
                      label="Race/Ethnicity"
                      placeholder="Select your race/ethnicity"
                      data={raceOptions}
                      value={personalInfo.race}
                      onChange={(value) => handlePersonalInfoChange('race', value)}
                      clearable
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <NumberInput
                      label="Number of Toes"
                      placeholder="Total number of toes"
                      min={0}
                      max={20}
                      value={personalInfo.numberOfToes}
                      onChange={(value) => handlePersonalInfoChange('numberOfToes', value)}
                    />
                  </Grid.Col>
                </Grid>

                <Textarea
                  label="Bio"
                  placeholder="Tell us about yourself..."
                  rows={4}
                  value={personalInfo.bio}
                  onChange={(e) => handlePersonalInfoChange('bio', e.currentTarget.value)}
                />
              </Stack>
            </Card>
            </Stack>
          </Tabs.Panel>

          {/* Subscription Tab */}
          <Tabs.Panel value="subscription" pt="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Subscription Status</Title>
                {subscriptionActive || subscriptionPastDue ? (
                  <>
                    <Alert color={subscriptionActive ? 'green' : 'yellow'} variant="light">
                      <Group gap="sm">
                        <IconCrown size="1.2rem" />
                        <div>
                          <Text fw={500}>
                            {subscriptionActive ? 'Active Subscription' : 'Payment Problem'}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {subscriptionPlanName} - {formatUsd(subscription.price_cents)}/month
                          </Text>
                          {subscriptionPastDue && (
                            <Text size="sm" mt={4}>
                              We couldn't renew your subscription.
                              {subscription.grace_until && new Date(subscription.grace_until) > new Date()
                                ? ` You keep access until ${new Date(subscription.grace_until).toLocaleDateString()} while the payment is retried.`
                                : ' Your access has lapsed.'}
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Alert>
                    <Text size="sm">
                      <strong>Started:</strong> {new Date(subscription.created_at).toLocaleDateString()}
                    </Text>
                    <Text size="sm">
                      <strong>Current Period Ends:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}
                    </Text>
                    <Text size="sm">
                      <strong>Status:</strong> {subscription.status}
                    </Text>
                    <Divider my="md" />
                    <Button
                      component="a"
                      href="/subscription"
                      variant="light"
                      leftSection={<IconCrown size="1rem" />}
                    >
                      Manage Subscription
                    </Button>
                  </>
                ) : (
                  <>
                    <Alert color="gray" variant="light">
                      <Text>You don't have an active subscription</Text>
                    </Alert>
                    <Text size="sm" c="dimmed">
                      Subscribe to unlock premium features and support your favorite Princesses!
                    </Text>
                    <Button
                      component="a"
                      href="/subscription"
                      variant="filled"
                      leftSection={<IconCrown size="1rem" />}
                    >
                      View Subscription Plans
                    </Button>
                  </>
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Demographics Tab */}
          <Tabs.Panel value="demographics" pt="lg">
            <Stack gap="lg">
              {/* Location */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Title order={4}>Location</Title>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Country"
                        placeholder="Your country"
                        value={demographics.country}
                        onChange={(e) => handleDemographicsChange('country', e.currentTarget.value)}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="State/Province"
                        placeholder="Your state or province"
                        value={demographics.state}
                        onChange={(e) => handleDemographicsChange('state', e.currentTarget.value)}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 8 }}>
                      <TextInput
                        label="City"
                        placeholder="Your city"
                        value={demographics.city}
                        onChange={(e) => handleDemographicsChange('city', e.currentTarget.value)}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <TextInput
                        label="ZIP/Postal Code"
                        placeholder="ZIP code"
                        value={demographics.zipCode}
                        onChange={(e) => handleDemographicsChange('zipCode', e.currentTarget.value)}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* Foot Attribute Categories */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <div>
                    <Title order={4}>Foot Attribute Categories</Title>
                    <Text size="sm" c="dimmed" mt={4}>
                      Pick up to 3 tags that best describe you — these are shown publicly on your profile.
                    </Text>
                  </div>
                  <Group gap="xs">
                    {demographics.categories.map(val => {
                      const attr = footAttributes.find(a => a.value === val);
                      return attr ? (
                        <Badge key={val} color="grape" variant="light" size="lg">
                          {attr.label}
                        </Badge>
                      ) : null;
                    })}
                    {demographics.categories.length === 0 && (
                      <Text size="sm" c="dimmed">No categories selected yet</Text>
                    )}
                  </Group>
                  <Chip.Group
                    multiple
                    value={demographics.categories}
                    onChange={(vals) => {
                      if (vals.length <= 3) {
                        handleDemographicsChange('categories', vals);
                      }
                    }}
                  >
                    <Group gap="xs">
                      {footAttributes.map((attr) => (
                        <Chip
                          key={attr.value}
                          value={attr.value}
                          color="grape"
                          size="sm"
                          disabled={
                            demographics.categories.length >= 3 &&
                            !demographics.categories.includes(attr.value)
                          }
                        >
                          {attr.label}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                  <Text size="xs" c="dimmed">
                    {demographics.categories.length}/3 selected
                  </Text>
                </Stack>
              </Card>

              {/* Lifestyle */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Title order={4}>Lifestyle & Interests</Title>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Occupation"
                        placeholder="What do you do for work?"
                        value={demographics.occupation}
                        onChange={(e) => handleDemographicsChange('occupation', e.currentTarget.value)}
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Workout Frequency"
                        placeholder="How often do you work out?"
                        data={workoutFrequencyOptions}
                        value={demographics.workoutFrequency}
                        onChange={(value) => handleDemographicsChange('workoutFrequency', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>

                  <MultiSelect
                    label="Hobbies"
                    placeholder="Select your hobbies"
                    data={hobbyOptions}
                    value={demographics.hobbies}
                    onChange={(value) => handleDemographicsChange('hobbies', value)}
                    searchable
                  />

                  <MultiSelect
                    label="Interests"
                    placeholder="What else are you interested in? (Type to add custom interests)"
                    data={hobbyOptions}
                    value={demographics.interests}
                    onChange={(value) => handleDemographicsChange('interests', value)}
                    searchable
                    creatable
                    getCreateLabel={(query) => `+ Add "${query}"`}
                  />

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Education Level"
                        placeholder="Highest education completed"
                        data={educationOptions}
                        value={demographics.education}
                        onChange={(value) => handleDemographicsChange('education', value)}
                        clearable
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <Select
                        label="Relationship Status"
                        placeholder="Your relationship status"
                        data={relationshipOptions}
                        value={demographics.relationshipStatus}
                        onChange={(value) => handleDemographicsChange('relationshipStatus', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Save Button */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="center">
            <Button
              leftSection={<IconDeviceFloppy size="1rem" />}
              size="lg"
              onClick={handleSaveProfile}
              loading={saving}
            >
              Save Profile
            </Button>
          </Group>
        </Card>
      </Stack>
    </Container>
    </ProtectedRoute>
  );
};

export default ProfilePage;
